import { DatePicker } from '@progress/kendo-react-dateinputs'
import { canAccessSegment } from 'app/utils'
import { DropdownComponent } from 'components/dropdown/component'
import { RSSFeedIcon } from 'components/icons/rssfeed'
import { TwitterIcon } from 'components/icons/twittersmall'
import { Loading } from 'components/loading'
import { RasaContext } from 'context'
import { Dataset } from 'generic/dataset'
import * as React from 'react'
import { Button, Input, Modal, ModalBody, ModalFooter, ModalHeader } from 'reactstrap'
import { DATEPICKER_DATE_FORMAT } from 'shared/constants'
import { BillingPlanDetailCode } from 'shared_server_client/types/billing_plan'
import { SourceTypes } from '../../../shared_server_client/constants'
import { isValidUpdatedSourceName } from '../utility/utils'
import './edit-source.scss'
import { LoadSegmentCodes } from 'components/common/load-partner-codes/component'
import { DEFAULT_SEGMENT_CODE } from '../constants'

const DATE_OPTIONS = [
  { key: '', description: 'No filter' },
  { key: 'today', description: 'Today' },
  { key: 'yesterday', description: 'Yesterday' },
  { key: '2 days ago', description: '2 days ago' },
  { key: '3 days ago', description: '3 days ago' },
  { key: '1 week ago', description: '1 week ago' },
  { key: '2 weeks ago', description: '2 weeks ago' },
  { key: '1 month ago', description: '1 month ago' },
  { key: '3 months ago', description: '3 months ago' },
  { key: '6 months ago', description: '6 months ago' },
  { key: '1 year ago', description: '1 year ago' },
  { key: 'custom', description: 'Custom date' },
]

export interface Source {
  source_type: string,
  name: string,
  source_url: string,
  id: string|number,
  community_partner_code: string,
  newsbrief_count?: number,
  subscription_options?: string,
}

interface EditSourceModalProps {
  isOpen: boolean,
  sourceData: Source,
  onClose: () => void,
  onSave: () => void,
}

interface EditSourceModalState {
  name: string,
  existingSources: any[],
  communityId?: string,
  hasSegmentAccess: boolean
  selectedSegmentCode: string,
  segments: any[],
  isLoading: boolean,
  isSaving: boolean,
  newsbriefCount: string,
  earliestDate: string,
  latestDate: string,
  earliestDateType: string,
  latestDateType: string,
  customEarliestDate: Date | null,
  customLatestDate: Date | null,
}

export class EditSourceModal extends React.Component<EditSourceModalProps, EditSourceModalState> {
  public static contextType = RasaContext
  public static key: string = 'editSourceModal'

  constructor(props: EditSourceModalProps) {
    super(props)
    this.state = {
      existingSources: [],
      hasSegmentAccess: false,
      isLoading: false,
      isSaving: false,
      name: '',
      selectedSegmentCode: DEFAULT_SEGMENT_CODE,
      segments: [],
      newsbriefCount: '',
      earliestDate: '',
      latestDate: '',
      earliestDateType: '',
      latestDateType: '',
      customEarliestDate: null,
      customLatestDate: null,
    }
  }

  public componentDidUpdate(prevProps: EditSourceModalProps) {
    // Load data when modal opens
    if (this.props.isOpen && !prevProps.isOpen && this.props.sourceData) {
      this.loadSourceData()
    }
  }

  private loadSourceData = () => {
    if (!this.props.sourceData || !this.props.sourceData.id) {
      return
    }

    this.setState({ isLoading: true })

    this.context.user.init().then(({activeCommunity}) => {
      const avlFeatures: BillingPlanDetailCode[] = activeCommunity.billingInfo.currentPlan.features || []

      Promise.all([
        new Dataset().loadCommunityDataset('communitySources', activeCommunity.communityId),
        canAccessSegment(avlFeatures, activeCommunity.communityId)
      ]).then(([sourcesResponse, canAccess]) => {
        const sources = sourcesResponse[0] || []

        const sourceOptions = this.props.sourceData.subscription_options ? JSON.parse(this.props.sourceData.subscription_options) : {}
        const dateFilter = sourceOptions.date_filter || {}

        // Determine if dates are predefined options or custom dates
        const earliestDateValue = dateFilter.earliest || ''
        const latestDateValue = dateFilter.latest || ''
        
        const earliestIsCustom = earliestDateValue && !DATE_OPTIONS.some(opt => opt.key === earliestDateValue)
        const latestIsCustom = latestDateValue && !DATE_OPTIONS.some(opt => opt.key === latestDateValue)

        this.setState({
          communityId: activeCommunity.communityId,
          hasSegmentAccess: canAccess,
          existingSources: sources,
          name: this.props.sourceData.name,
          selectedSegmentCode: this.props.sourceData.community_partner_code || DEFAULT_SEGMENT_CODE,
          newsbriefCount: this.props.sourceData.newsbrief_count != null && this.props.sourceData.newsbrief_count !== -1
            ? String(this.props.sourceData.newsbrief_count)
            : '',
          earliestDate: earliestDateValue,
          latestDate: latestDateValue,
          earliestDateType: earliestIsCustom ? 'custom' : earliestDateValue,
          latestDateType: latestIsCustom ? 'custom' : latestDateValue,
          customEarliestDate: earliestIsCustom ? new Date(earliestDateValue) : null,
          customLatestDate: latestIsCustom ? new Date(latestDateValue) : null,
          isLoading: false,
        })
      })
    })
  }

  public render() {
    if (!this.props.isOpen || !this.props.sourceData) {
      return null
    }

    return (
      <Modal isOpen={this.props.isOpen} toggle={this.props.onClose} size="lg">
        <ModalHeader toggle={this.props.onClose}>
          Edit Source
        </ModalHeader>
        <ModalBody>
          {this.renderContent()}
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" onClick={this.props.onClose} disabled={this.state.isSaving}>
            Cancel
          </Button>
          <Button
            color="primary"
            onClick={this.handleSave}
            disabled={this.state.isSaving || this.saveDisabled()}
          >
            {this.state.isSaving ? 'Saving...' : 'Save'}
          </Button>
        </ModalFooter>
      </Modal>
    )
  }

  private renderContent() {
    if (this.state.isLoading) {
      return (
        <div style={{ display: 'flex', justifyContent: 'center', padding: '40px' }}>
          <Loading size="64"/>
        </div>
      )
    }

    return (
      <div className="edit-source-modal-content">
        <div style={{ display: 'flex', gap: '5px', marginBottom: '15px' }}>
          <div className="heading" style={{ flexShrink: 0, width: '150px' }}>
            <h5>Source Type</h5>
          </div>
          <div style={{ flexGrow: 1 }}>
            <div style={{ display: 'flex', alignItems: 'center', paddingTop: '8px' }}>
              {this.props.sourceData.source_type === 'Twitter' ?
              <TwitterIcon svgwidth={20} svgheight={20}/> :
              <RSSFeedIcon svgwidth={20} svgheight={20}/> }
              <strong style={{ marginLeft: '8px' }}>{this.props.sourceData.source_type}</strong>
            </div>
          </div>
        </div>

        <div style={{ display: 'flex', gap: '5px', marginBottom: '15px' }}>
          <div className="heading" style={{ flexShrink: 0, width: '150px' }}>
            <h5>Source URL</h5>
          </div>
          <div style={{ flexGrow: 1, paddingTop: '8px' }}>
            <a href={this.sourceUrl()} target="_blank" rel="noopener">{this.sourceUrlText()}</a>
          </div>
        </div>

        <div style={{ display: 'flex', gap: '5px', marginBottom: '15px' }}>
          <div className="heading" style={{ flexShrink: 0, width: '150px' }}>
            <h5>Source Name</h5>
          </div>
          <div style={{ flexGrow: 1 }}>
            <Input
              type="text"
              value={this.state.name}
              onChange={(e) => this.setState({name: e.target.value})}
            />
            {!this.state.isLoading && !!this.state.existingSources.length &&
            !isValidUpdatedSourceName(this.state.existingSources, this.state.name, String(this.props.sourceData.id)) &&
            <div style={{ color: '#dc3545', fontSize: '12px', marginTop: '4px' }}>
              You already have a source with this name
            </div>}
          </div>
        </div>

        {this.state.hasSegmentAccess &&
          <div style={{ display: 'flex', gap: '5px', marginBottom: '15px' }}>
            <div className="heading" style={{ flexShrink: 0, width: '150px' }}>
              <h5>Segment</h5>
            </div>
            <div style={{ flexGrow: 1 }}>
              <LoadSegmentCodes
                segmentCode={this.state.selectedSegmentCode}
                setSegmentCode={this.segmentCodeChanged}
                setSegmentData={this.setSegmentData}
                hideLabel={true}
              />
            </div>
          </div>
        }

        <div style={{ display: 'flex', gap: '5px', marginBottom: '15px' }}>
          <div className="heading" style={{ flexShrink: 0, width: '150px' }}>
            <h5>Max Articles</h5>
          </div>
          <div style={{ flexGrow: 1 }}>
            <Input
              type="number"
              value={this.state.newsbriefCount}
              onChange={this.handleNewsbriefCountChange}
              placeholder="Leave empty for no limit"
            />
          </div>
        </div>

        <div style={{ display: 'flex', gap: '5px', marginBottom: '15px' }}>
          <div className="heading" style={{ flexShrink: 0, width: '150px' }}>
            <h5>Earliest Date</h5>
          </div>
          <div style={{ flexGrow: 1 }}>
            <DropdownComponent
              data={this.getFilteredEarliestDateOptions()}
              selected={this.state.earliestDateType}
              onChange={this.handleEarliestDateTypeChange}
            />
            {this.state.earliestDateType === 'custom' && (
              <div style={{ marginTop: '8px' }}>
                <DatePicker
                  className="modal-schedule-date-picker"
                  format={DATEPICKER_DATE_FORMAT}
                  value={this.state.customEarliestDate}
                  onChange={this.handleCustomEarliestDateChange}
                  max={this.state.latestDateType === 'custom' ? this.state.customLatestDate : 
                       this.state.latestDateType ? this.getDateFromOption(this.state.latestDateType) : undefined}
                />
              </div>
            )}
          </div>
        </div>

        <div style={{ display: 'flex', gap: '5px', marginBottom: '15px' }}>
          <div className="heading" style={{ flexShrink: 0, width: '150px' }}>
            <h5>Latest Date</h5>
          </div>
          <div style={{ flexGrow: 1 }}>
            <DropdownComponent
              data={this.getFilteredLatestDateOptions()}
              selected={this.state.latestDateType}
              onChange={this.handleLatestDateTypeChange}
            />
            {this.state.latestDateType === 'custom' && (
              <div style={{ marginTop: '8px' }}>
                <DatePicker
                  className="modal-schedule-date-picker"
                  format={DATEPICKER_DATE_FORMAT}
                  value={this.state.customLatestDate}
                  onChange={this.handleCustomLatestDateChange}
                  min={this.state.earliestDateType === 'custom' ? this.state.customEarliestDate : 
                       this.state.earliestDateType ? this.getDateFromOption(this.state.earliestDateType) : undefined}
                />
              </div>
            )}
          </div>
        </div>
      </div>
    )
  }

  private segmentCodeChanged = (code: any) => {
    this.setState({
      selectedSegmentCode: code.key,
    })
  }

  private setSegmentData = (segments: any[]) => {
    this.setState({
      segments,
    })
  }

  private handleNewsbriefCountChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value
    // Allow empty string or valid integers only
    if (value === '' || /^\d+$/.test(value)) {
      this.setState({ newsbriefCount: value })
    }
  }

  private handleEarliestDateTypeChange = (selection: any) => {
    const selectedKey = selection.selected.key
    this.setState({ 
      earliestDateType: selectedKey,
      earliestDate: selectedKey === 'custom' ? '' : selectedKey,
      customEarliestDate: selectedKey === 'custom' ? null : null
    })
  }

  private handleLatestDateTypeChange = (selection: any) => {
    const selectedKey = selection.selected.key
    this.setState({ 
      latestDateType: selectedKey,
      latestDate: selectedKey === 'custom' ? '' : selectedKey,
      customLatestDate: selectedKey === 'custom' ? null : null
    })
  }

  private handleCustomEarliestDateChange = (e: any) => {
    const date = e.target.value
    this.setState({ 
      customEarliestDate: date,
      earliestDate: date ? date.toISOString().split('T')[0] : ''
    })
  }

  private handleCustomLatestDateChange = (e: any) => {
    const date = e.target.value
    this.setState({ 
      customLatestDate: date,
      latestDate: date ? date.toISOString().split('T')[0] : ''
    })
  }

  private getDateFromOption = (option: string): Date | null => {
    if (!option || option === '' || option === 'custom') return null
    
    const now = new Date()
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate())
    
    switch (option) {
      case 'today': return today
      case 'yesterday': return new Date(today.getTime() - 24 * 60 * 60 * 1000)
      case '2 days ago': return new Date(today.getTime() - 2 * 24 * 60 * 60 * 1000)
      case '3 days ago': return new Date(today.getTime() - 3 * 24 * 60 * 60 * 1000)
      case '1 week ago': return new Date(today.getTime() - 7 * 24 * 60 * 60 * 1000)
      case '2 weeks ago': return new Date(today.getTime() - 14 * 24 * 60 * 60 * 1000)
      case '1 month ago': {
        const date = new Date(today)
        date.setMonth(date.getMonth() - 1)
        return date
      }
      case '3 months ago': {
        const date = new Date(today)
        date.setMonth(date.getMonth() - 3)
        return date
      }
      case '6 months ago': {
        const date = new Date(today)
        date.setMonth(date.getMonth() - 6)
        return date
      }
      case '1 year ago': {
        const date = new Date(today)
        date.setFullYear(date.getFullYear() - 1)
        return date
      }
      default: return null
    }
  }

  private getFilteredEarliestDateOptions = () => {
    if (!this.state.latestDateType || this.state.latestDateType === '') {
      return DATE_OPTIONS
    }

    const latestDate = this.state.latestDateType === 'custom' 
      ? this.state.customLatestDate 
      : this.getDateFromOption(this.state.latestDateType)

    if (!latestDate) return DATE_OPTIONS

    return DATE_OPTIONS.filter(option => {
      if (option.key === '' || option.key === 'custom') return true
      const optionDate = this.getDateFromOption(option.key)
      return !optionDate || optionDate <= latestDate
    })
  }

  private getFilteredLatestDateOptions = () => {
    if (!this.state.earliestDateType || this.state.earliestDateType === '') {
      return DATE_OPTIONS
    }

    const earliestDate = this.state.earliestDateType === 'custom'
      ? this.state.customEarliestDate
      : this.getDateFromOption(this.state.earliestDateType)

    if (!earliestDate) return DATE_OPTIONS

    return DATE_OPTIONS.filter(option => {
      if (option.key === '' || option.key === 'custom') return true
      const optionDate = this.getDateFromOption(option.key)
      return !optionDate || optionDate >= earliestDate
    })
  }

  private sourceUrlText() {
    switch (this.props.sourceData.source_type) {
      case SourceTypes.twitter : return `@${this.props.sourceData.source_url}`
      default : return this.props.sourceData.source_url
    }
  }

  private sourceUrl() {
    switch (this.props.sourceData.source_type) {
      case SourceTypes.rss : return this.props.sourceData.source_url
      case SourceTypes.twitter : return `https://www.twitter.com/${this.props.sourceData.source_url}`
      default: return null
    }
  }

  private saveDisabled(): boolean {
    return !this.state.isLoading && !!this.state.existingSources.length &&
           !isValidUpdatedSourceName(this.state.existingSources, this.state.name, String(this.props.sourceData.id))
  }

  private handleSave = () => {
    this.setState({ isSaving: true })

    this.context.entityMetadata.getEntityObject('communitySource')
      .then((entity) => {
        entity.setRecordId(this.state.communityId, this.props.sourceData.id)
        entity.data.name = this.state.name
        if (this.state.selectedSegmentCode) {
          entity.data.community_partner_code = this.state.selectedSegmentCode
        }
        // Set newsbrief_count: -1 if empty, otherwise parse as integer
        entity.data.newsbrief_count = this.state.newsbriefCount === '' ? -1 : parseInt(this.state.newsbriefCount, 10)
        
        // Build options object with date filter
        const options: any = {}
        const finalEarliestDate = this.state.earliestDateType === 'custom' 
          ? (this.state.customEarliestDate ? this.state.customEarliestDate.toISOString().split('T')[0] : '')
          : this.state.earliestDate
        const finalLatestDate = this.state.latestDateType === 'custom'
          ? (this.state.customLatestDate ? this.state.customLatestDate.toISOString().split('T')[0] : '')
          : this.state.latestDate
          
        if (finalEarliestDate || finalLatestDate) {
          options.date_filter = {}
          if (finalEarliestDate) {
            options.date_filter.earliest = finalEarliestDate
          }
          if (finalLatestDate) {
            options.date_filter.latest = finalLatestDate
          }
        }
        
        // Store options as JSON string
        entity.data.options = Object.keys(options).length > 0 ? JSON.stringify(options) : null
        
        return entity.save()
      })
      .then(() => {
        this.setState({ isSaving: false })
        this.props.onSave()
      })
      .catch(() => {
        this.setState({ isSaving: false })
      })
  }
}
